import * as z from "zod";
import {
  canReadBranch,
  canReadWorkflow,
  userFacingError,
} from "../authorization";
import { zql } from "../generatedDrizzleSchema";
import { clientGeneratableIdSchema } from "../id";
import { mutator } from "./mutator";

const setChatFocusSchema = z.object({
  id: z.string(),
  entryId: clientGeneratableIdSchema,
  workflowId: z.string(),
  // Null focuses the workflow’s live version.
  branchId: z.string().nullable(),
  // Display names for the optimistic marker; the server records its own.
  workflowName: z.string(),
  branchName: z.string().nullable(),
});

export const setChatFocus = mutator({
  schema: setChatFocusSchema,
  description:
    "Records that a workflow’s live version or one of its branches was opened in a chat.",
  interactionType: "update",
  resolve: {},
  permissions: {
    none: "personal chats are read and written only for ctx.userId; the workflow and branch are checked with canReadWorkflow and canReadBranch",
  },
  fn: async ({ tx, ctx, args }) => {
    const [pc] = await tx.run(
      zql.personalChats
        .where("deletedAt", "IS", null)
        .where("id", args.id)
        .where("userId", ctx.userId)
    );
    if (!pc) return;
    const workflowOpenedAt = Date.now();
    if (pc.workflowId === args.workflowId && pc.branchId === args.branchId) {
      await tx.mutate.personalChats.update({ id: args.id, workflowOpenedAt });
      return;
    }
    let { workflowName, branchName } = args;
    // Clients may not have the workflow’s permission rows synced, so only the server checks access.
    if (tx.location === "server") {
      const workflow = await tx.run(
        zql.workflows
          .where("id", args.workflowId)
          .where("deletedAt", "IS", null)
          .where(canReadWorkflow(ctx))
          .one()
      );
      if (!workflow) throw userFacingError("Workflow not found");
      workflowName = workflow.name;
      if (args.branchId) {
        const branch = await tx.run(
          zql.workflowBranches
            .where("id", args.branchId)
            .where("workflowId", workflow.id)
            .where(canReadBranch(ctx))
            .one()
        );
        if (!branch) throw userFacingError("Branch not found");
        branchName = branch.name;
      }
    }
    const [last] = await tx.run(
      zql.chatEntries
        .where("chatId", pc.chatId)
        .orderBy("createdAt", "desc")
        .limit(1)
    );
    await tx.mutate.chatEntries.insert({
      id: args.entryId,
      chatId: pc.chatId,
      entryType: "UserAction",
      createdAt: Date.now(),
      previousEntryId: last?.id ?? null,
      streaming: false,
      generationId: null,
      tenantId: ctx.tenantId,
    });
    await tx.mutate.userActions.insert({
      entryId: args.entryId,
      userId: ctx.userId,
      actionType: "focusWorkflow",
      metadata: {
        workflowId: args.workflowId,
        workflowName,
        branchId: args.branchId,
        branchName: args.branchId ? branchName : null,
        by: "user",
      },
      tenantId: ctx.tenantId,
    });
    await tx.mutate.personalChats.update({
      id: args.id,
      workflowId: args.workflowId,
      branchId: args.branchId,
      workflowOpenedAt,
    });
  },
});
